import { useContext, useMemo } from "react";
import { AuthContext, AuthContextType } from "./customHooks";
import {
  Permission,
  hasEffectivePermission,
} from "@eip/shared/types/permissions";
import {
  EffectiveAccess,
  getEffectiveAccess,
  isAdminLevelRole,
  isSuperAdmin,
} from "@eip/shared/types/regionAccess";
import { extractRegionFromSowNumber } from "@eip/shared/types/sowRegion";

const NO_ACCESS: EffectiveAccess = {
  visibleRegions: [],
  canImpersonate: false,
  canAudit: false,
  auditableRegions: [],
};

export type UseAccessResult = {
  /** Region-scoped access summary (visibleRegions: null = all regions). */
  access: EffectiveAccess;
  /** Effective permission check (role + region aware — matches the server gate). */
  hasPermission: (permission: Permission) => boolean;
  /**
   * Can the user audit THIS SoW? Home-region only (mirrors the server's
   * assertSowRegionAuditable). Legacy SoWs without a sowNumber render the
   * affordance and let the server's owning-admin fallback decide.
   */
  canAuditSow: (sowNumber: string | undefined) => boolean;
  isAdminLevel: boolean;
  isSuperAdmin: boolean;
  /**
   * View Legacy lens entitlement — server-computed from the REAL user via
   * /user-info (NOT getEffectiveAccess: during View As the context holds the
   * impersonated role, which is the wrong input for this gate).
   */
  canViewLegacy: boolean;
};

/**
 * Role/region access for conditional rendering, resolved through the SAME
 * shared effective-permission layer the server's requirePermission middleware
 * uses — so UI gating cannot drift from the API gate.
 */
export function useAccess(): UseAccessResult {
  const auth = useContext(AuthContext) as AuthContextType | null;
  const userRole = auth?.userRole ?? null;
  const userRegion = auth?.userRegion || null;
  // Fail-closed: while the env signal is unknown (loading/logged out), assume
  // production so View As never flashes for roles that only qualify non-prod.
  const isProduction = auth?.isProduction ?? true;
  const canViewLegacy = auth?.canViewLegacy ?? false;

  return useMemo(() => {
    if (!userRole || !userRegion) {
      return {
        access: NO_ACCESS,
        hasPermission: () => false,
        canAuditSow: () => false,
        isAdminLevel: false,
        isSuperAdmin: false,
        canViewLegacy: false,
      };
    }
    const access = getEffectiveAccess(userRole, userRegion, { isProduction });
    return {
      access,
      hasPermission: (permission: Permission) => hasEffectivePermission(userRole, userRegion, permission),
      canAuditSow: (sowNumber: string | undefined) => {
        if (!access.canAudit) return false;
        const sowRegion = extractRegionFromSowNumber(sowNumber);
        return sowRegion === null || access.auditableRegions.includes(sowRegion);
      },
      isAdminLevel: isAdminLevelRole(userRole),
      isSuperAdmin: isSuperAdmin(userRole),
      canViewLegacy,
    };
  }, [userRole, userRegion, isProduction, canViewLegacy]);
}
